import React, { useEffect, useState, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { toast } from 'sonner';
import { LifeStoryCard } from './LifeStoryCard';
import { AIMemoryMoments } from './AIMemoryMoments';

export function AITab({ memorial, memorialId, isOwner, onChange }) {
  const [enabled, setEnabled] = useState(!!memorial.ai_enabled);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({ job_type: 'intro_video', title: '', description: '' });

  const load = useCallback(() => api.get(`/memorials/${memorialId}/ai/jobs`).then(({ data }) => setJobs(data.jobs)), [memorialId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (v) => {
    if (!isOwner) { alert('Only the owner can change AI settings'); return; }
    await api.post(`/memorials/${memorialId}/ai/opt-in`, { enabled: v });
    setEnabled(v); onChange?.();
  };

  const createJob = async () => {
    if (!form.title) return;
    try {
      await api.post(`/memorials/${memorialId}/ai/jobs`, form);
      setForm({ job_type: 'intro_video', title: '', description: '' });
      toast.success('Memory moment saved — AI generation coming soon.');
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="ml-card">
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">AI Memory Moments</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
          AI memories are <strong>optional</strong> and should only be created when the family agrees. We never imply the deceased is being "brought back" — these are gentle tributes built from photos and stories you provide.
        </p>
        <div className="mt-5 flex items-center gap-4">
          <span className="text-sm">AI opt-in</span>
          <button onClick={() => toggle(!enabled)} className={`relative inline-flex h-6 w-12 items-center rounded-full ${enabled ? 'bg-[hsl(var(--accent))]' : 'bg-[hsl(var(--muted))]'}`} data-testid="ai-toggle">
            <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${enabled ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
          <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{enabled ? 'Enabled' : 'Disabled'}</span>
        </div>
      </div>

      {enabled && (
        <LifeStoryCard memorialId={memorialId} />
      )}

      {enabled && (
        <AIMemoryMoments memorialId={memorialId} />
      )}

      {enabled && (
        <div className="ml-card">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Queue a memory moment</h3>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <select className="ml-input" value={form.job_type} onChange={(e) => setForm({ ...form, job_type: e.target.value })} data-testid="ai-type">
              <option value="intro_video">Life story intro video</option>
              <option value="photo_animation">Photo animation</option>
              <option value="memory_moment">Memory moment</option>
              <option value="voice_narration">Voice narration</option>
            </select>
            <input className="ml-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ai-title" />
            <textarea rows={3} className="ml-input sm:col-span-2" placeholder="Describe the memory" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <button onClick={createJob} className="ml-btn-primary mt-3" data-testid="ai-create-btn">Queue memory moment</button>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Memory moments</h3>
          {jobs.map((j) => (
            <div key={j.job_id} className="ml-card">
              <div className="flex justify-between items-start gap-4">
                <div>
                  <div className="ml-overline">{j.job_type.replace('_', ' ')}</div>
                  <div className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">{j.title}</div>
                  {j.description && <p className="text-sm mt-2 text-[hsl(var(--muted-foreground))]">{j.description}</p>}
                </div>
                <span className="ml-overline !text-[hsl(var(--accent))] shrink-0">{j.status}</span>
              </div>
              {j.note && <p className="mt-3 text-[11px] text-[hsl(var(--muted-foreground))] italic">{j.note}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
